import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import type { BlockType } from "@/types/block";
import { PageHtmlEditor } from "@/routes/pages/edit/PageHtmlEditor";
import type { PageAccessSettingsState } from "@/routes/pages/edit/AccessRequestsTab";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import type { PageOnSubmitActionType } from "@/types/pageFormSubmit";
import type { AppliedLinkInBioPage } from "@/routes/pages/edit/TemplatesTab";

const DEFAULT_ACCESS: PageAccessSettingsState = {
  enabled: false,
  sharedLeadIds: [],
  allowAccessRequests: false,
  autoGrantAccessRequests: false,
};

function normalizeAccess(access: unknown): PageAccessSettingsState {
  if (!access || typeof access !== "object") {
    return { ...DEFAULT_ACCESS };
  }
  const value = access as Record<string, unknown>;
  return {
    enabled: value.enabled === true,
    sharedLeadIds: Array.isArray(value.sharedLeadIds)
      ? value.sharedLeadIds.map((id) => String(id)).filter(Boolean)
      : [],
    allowAccessRequests: value.allowAccessRequests === true,
    autoGrantAccessRequests: value.autoGrantAccessRequests === true,
  };
}

interface Page {
  _id: string;
  title: string;
  route: string;
  type?: "block" | "html" | "markdown" | "redirect";
  blocks: BlockType[];
  html?: string;
  redirectUrl?: string;
  backgroundImageUrl?: string;
  pageBackgroundColor?: string;
  pageFontFamily?: string;
  pageTextColor?: string;
  pageLinkColor?: string;
  pageTitle?: string;
  metaImageUrl?: string;
  onSubmit?: Array<{ type: "CreateLead" } | { type: "StartAutomation"; automationId: string }>;
  formOnSubmit?: Record<string, PageOnSubmitActionType[]>;
  access?: PageAccessSettingsState;
}

export function PageEdit() {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState<Page | null>(null);
  const [blocks, setBlocks] = useState<BlockType[]>([]);
  const [title, setTitle] = useState<string>("");
  const [route, setRoute] = useState<string>("");
  const [pageTitle, setPageTitle] = useState<string>("");
  const [metaImageUrl, setMetaImageUrl] = useState<string>("");
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>("");
  const [pageBackgroundColor, setPageBackgroundColor] = useState<string>("");
  const [pageFontFamily, setPageFontFamily] = useState<string>("");
  const [pageTextColor, setPageTextColor] = useState<string>("");
  const [pageLinkColor, setPageLinkColor] = useState<string>("");
  const [pageOnSubmitActions, setPageOnSubmitActions] = useState<Array<{ type: "CreateLead" } | { type: "StartAutomation"; automationId: string }>>([]);
  const [formOnSubmit, setFormOnSubmit] = useState<Record<string, PageOnSubmitActionType[]>>({});
  const [access, setAccess] = useState<PageAccessSettingsState>({ ...DEFAULT_ACCESS });
  const [html, setHtml] = useState<string>("");
  const [redirectUrl, setRedirectUrl] = useState<string>("");
  const [pageType, setPageType] = useState<"html" | "markdown" | "redirect">("html");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      fetch(`/api/pages/${id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.page) {
            setPage(data.page);
            setBlocks(data.page.blocks || []);
            setHtml(data.page.html || "");
            setTitle(data.page.title || "");
            setRoute(data.page.route || "");
            setPageTitle(data.page.pageTitle || "");
            setMetaImageUrl(data.page.metaImageUrl || "");
            setBackgroundImageUrl(data.page.backgroundImageUrl || "");
            setPageBackgroundColor(data.page.pageBackgroundColor || "");
            setPageFontFamily(data.page.pageFontFamily || "");
            setPageTextColor(data.page.pageTextColor || "");
            setPageLinkColor(data.page.pageLinkColor || "");
            setPageOnSubmitActions(data.page.onSubmit || []);
            setFormOnSubmit(data.page.formOnSubmit || {});
            setAccess(normalizeAccess(data.page.access));
            setRedirectUrl(data.page.redirectUrl || "");
            setPageType(
              data.page.type === "markdown"
                ? "markdown"
                : data.page.type === "redirect"
                  ? "redirect"
                  : "html"
            );
            setBreadcrumbs([
              { label: "Pages", path: "/pages" },
              { label: data.page.title, path: `/pages/${id}/edit` },
            ]);
          }
        })
        .catch((error) => {
          console.error("Error loading page:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    return () => {
      clearBreadcrumbs();
    };
  }, [id]);

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    if (id) {
      setBreadcrumbs([
        { label: "Pages", path: "/pages" },
        { label: newTitle, path: `/pages/${id}/edit` },
      ]);
    }
  };

  const savedAccess = normalizeAccess(page?.access);

  const hasUnsavedChanges = page
    ? html !== (page.html ?? "") ||
      title !== (page.title || "") ||
      route !== (page.route || "") ||
      pageTitle !== (page.pageTitle || "") ||
      metaImageUrl !== (page.metaImageUrl || "") ||
      redirectUrl !== (page.redirectUrl ?? "") ||
      pageType !==
        (page.type === "markdown"
          ? "markdown"
          : page.type === "redirect"
            ? "redirect"
            : "html") ||
      JSON.stringify(formOnSubmit) !== JSON.stringify(page.formOnSubmit || {}) ||
      JSON.stringify(access) !== JSON.stringify(savedAccess)
    : false;

  const handleSave = async (htmlOverride?: string) => {
    if (!id) return;

    setIsSaving(true);
    try {
      const htmlToSave = typeof htmlOverride === "string" ? htmlOverride : html;
      const body = {
        blocks,
        route,
        title,
        type: pageType,
        html: htmlToSave,
        redirectUrl,
        pageTitle,
        metaImageUrl,
        backgroundImageUrl,
        pageBackgroundColor,
        pageFontFamily,
        pageTextColor,
        pageLinkColor,
        onSubmit: pageOnSubmitActions,
        formOnSubmit,
        access,
      };
      const response = await fetch(`/api/pages/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error("Failed to save page");
      }
      if (htmlOverride !== undefined) {
        setHtml(htmlOverride);
      }
      setPage((prev) => (prev ? { ...prev, ...body } : null));
    } catch (error) {
      console.error("Error saving page:", error);
      alert("Failed to save page");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTemplateApplied = (updatedPage: AppliedLinkInBioPage) => {
    const nextHtml = updatedPage.html ?? "";
    const nextTitle = updatedPage.title || "";
    const nextPageTitle = updatedPage.pageTitle || "";
    setHtml(nextHtml);
    setTitle(nextTitle);
    setPageTitle(nextPageTitle);
    setPageType("html");
    setPage((prev) =>
      prev
        ? {
            ...prev,
            html: nextHtml,
            title: nextTitle,
            pageTitle: nextPageTitle,
            type: "html",
          }
        : null
    );
    if (id) {
      setBreadcrumbs([
        { label: "Pages", path: "/pages" },
        { label: nextTitle || "Page", path: `/pages/${id}/edit` },
      ]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div>Page not found</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden w-full" style={{ height: "calc(100vh - 64px)" }}>
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <PageHtmlEditor
          pageId={id}
          html={html}
          onHtmlChange={setHtml}
          title={title}
          route={route}
          onTitleChange={handleTitleChange}
          onRouteChange={setRoute}
          pageType={pageType}
          onPageTypeChange={setPageType}
          redirectUrl={redirectUrl}
          onRedirectUrlChange={setRedirectUrl}
          formOnSubmit={formOnSubmit}
          onFormOnSubmitChange={setFormOnSubmit}
          access={access}
          onAccessChange={setAccess}
          onSave={handleSave}
          isSaving={isSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          onTemplateApplied={handleTemplateApplied}
        />
      </div>
    </div>
  );
}
